import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { Dashboard } from './pages/Dashboard';
import { Transcripts } from './pages/Transcripts';
import { Students } from './pages/Students';
import { StudentDetail } from './pages/StudentDetail';
import { ApiDocs } from './pages/ApiDocs';
import { ApiKeys } from './pages/ApiKeys';
import { Partners } from './pages/Partners';
import { PartnerDetail } from './pages/PartnerDetail';
import { NotFound } from './pages/NotFound';
import { UserManagement } from './pages/UserManagement';
import { ClientUserManagement } from './pages/ClientUserManagement';
import Login from './pages/Login';
import AuthAction from './pages/AuthAction';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './components/ProtectedRoute';
import { RoleGuard } from './components/RoleGuard';
import { useAuth } from './contexts/AuthContext';

/** Roles whose home is the data dashboard. */
const DASHBOARD_ROLES = [
  'transcript_viewer',
  'academic_advisor',
  'partner',
  'sophia_partner_admin',
  'sophia_admin',
  'client_admin',
];

/**
 * Users whose only surfaces are API Keys / API Docs (api_developer without
 * any data role) land on /api-keys; everyone else lands on the dashboard.
 */
function DefaultLanding(): React.ReactElement {
  const { roles } = useAuth();
  const hasDashboardRole = DASHBOARD_ROLES.some((role) => roles.includes(role));
  const target = !hasDashboardRole && roles.includes('api_developer') ? '/api-keys' : '/dashboard';
  return <Navigate to={target} replace />;
}

function App(): React.ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/action" element={<AuthAction />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/transcripts" element={<Transcripts />} />
        <Route path="/students" element={<Students />} />
        <Route path="/students/:id" element={<StudentDetail />} />
        <Route
          path="/partners"
          element={
            <RoleGuard requiredRole="sophia_partner_admin" fallback={<NotFound />}>
              <Partners />
            </RoleGuard>
          }
        />
        <Route
          path="/partners/:id"
          element={
            <RoleGuard requiredRole="sophia_partner_admin" fallback={<NotFound />}>
              <PartnerDetail />
            </RoleGuard>
          }
        />
        <Route path="/api-keys" element={<ApiKeys />} />
        <Route
          path="/api-docs"
          element={
            <RoleGuard requiredRole="api_developer" fallback={<NotFound />}>
              <ApiDocs />
            </RoleGuard>
          }
        />
        <Route
          path="/users"
          element={
            <RoleGuard requiredRole="sophia_admin" fallback={<NotFound />}>
              <UserManagement />
            </RoleGuard>
          }
        />
        <Route
          path="/manage-users"
          element={
            <RoleGuard
              requiredRole={['client_admin', 'client_user_management']}
              fallback={<NotFound />}
            >
              <ClientUserManagement />
            </RoleGuard>
          }
        />
        <Route path="/" element={<DefaultLanding />} />
        <Route path="*" element={<DefaultLanding />} />
      </Route>
    </Routes>
  );
}

export default App;
